import { useEffect, useState } from 'react';
import { adminApi } from '../../../services/adminApi';

interface UserRow {
  id: string;
  email: string;
  username: string;
  walletAddress: string | null;
  createdAt: string;
  deletedAt: string | null;
  deletionReason: string | null;
  deletionRestriction: string | null;
  chips: string; // micro
  activeSeats: Array<{
    gameId: string;
    gameName: string;
    gameStatus: string;
    chipStack: string;
    position: string;
  }>;
}

const RESTRICTIONS = [
  { value: 'full_lock', label: 'Full lock (cannot log in)' },
  { value: 'withdraw_only', label: 'Withdraw only (can cash out remaining chips)' },
  { value: 'password_reset_only', label: 'Password reset only' },
];

function microToChips(micro: string): string {
  return (Number(micro) / 1_000_000).toFixed(2);
}

export default function UsersTab() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [query, setQuery] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<UserRow | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const res = await adminApi.get('/users', {
        params: { q: query || undefined, includeDeleted: includeDeleted ? '1' : undefined },
      });
      setUsers(res.data.users);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeleted]);

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && refresh()}
          placeholder="Search email / username / id / wallet"
          className="flex-1 min-w-[240px] px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-sm"
        />
        <label className="flex items-center gap-2 text-sm text-gray-300 select-none">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />
          Include soft-deleted
        </label>
        <button
          onClick={refresh}
          className="px-3 py-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg"
        >
          {loading ? 'Searching\u2026' : 'Search'}
        </button>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black/30 text-gray-400 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Username</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-right px-3 py-2">Chips (mUSD)</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Seated</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-3 py-2 font-medium">{u.username}</td>
                <td className="px-3 py-2 text-gray-300">{u.email}</td>
                <td className="px-3 py-2 text-right tabular-nums">{microToChips(u.chips)}</td>
                <td className="px-3 py-2">
                  {u.deletedAt ? (
                    <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30">
                      {u.deletionRestriction ?? 'deleted'}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      active
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-gray-400">
                  {u.activeSeats.length > 0
                    ? `${u.activeSeats.length} seat${u.activeSeats.length > 1 ? 's' : ''}`
                    : '\u2014'}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => setSelected(u)}
                    className="px-2 py-1 text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded"
                  >
                    Manage
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-gray-400">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <UserDetailModal
          user={selected}
          onClose={() => setSelected(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

function UserDetailModal({
  user,
  onClose,
  onChanged,
}: {
  user: UserRow;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [restriction, setRestriction] = useState('full_lock');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function adjust(sign: 1 | -1) {
    setErr(null);
    setMsg(null);
    if (!amount || !reason) {
      setErr('Amount and reason are both required.');
      return;
    }
    const n = parseFloat(amount);
    if (!isFinite(n) || n <= 0) {
      setErr('Amount must be a positive number.');
      return;
    }
    setBusy(true);
    try {
      const res = await adminApi.post(`/users/${user.id}/adjust-chips`, {
        amount: sign * n,
        reason,
      });
      setMsg(
        `OK. New balance: ${(Number(res.data.balanceAfter) / 1_000_000).toFixed(2)} mUSD`
      );
      setAmount('');
      setReason('');
      onChanged();
    } catch (e: any) {
      setErr(e.response?.data?.message ?? e.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function softDelete() {
    setErr(null);
    setMsg(null);
    if (!reason) {
      setErr('Reason is required for soft-delete.');
      return;
    }
    if (!confirm(`Soft-delete ${user.email}? This is reversible via Restore.`)) return;
    setBusy(true);
    try {
      await adminApi.post(`/users/${user.id}/soft-delete`, { reason, restriction });
      setMsg('User soft-deleted.');
      onChanged();
      onClose();
    } catch (e: any) {
      setErr(e.response?.data?.message ?? e.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    setErr(null);
    setMsg(null);
    if (!confirm(`Restore ${user.email}?`)) return;
    setBusy(true);
    try {
      await adminApi.post(`/users/${user.id}/restore`);
      setMsg('User restored.');
      onChanged();
      onClose();
    } catch (e: any) {
      setErr(e.response?.data?.message ?? e.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-20 bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-brand-bg border border-white/10 rounded-2xl p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold">{user.username}</h3>
            <div className="text-sm text-gray-400">{user.email}</div>
            <div className="text-xs text-gray-500 mt-1">id: {user.id}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">
            \u00d7
          </button>
        </div>

        <div className="bg-black/30 rounded-lg px-3 py-2 text-sm">
          Balance:{' '}
          <span className="font-bold tabular-nums">
            {(Number(user.chips) / 1_000_000).toFixed(2)} mUSD
          </span>
          {user.activeSeats.length > 0 && (
            <span className="ml-3 text-yellow-300">
              \u26a0 Seated at {user.activeSeats.length} active game(s)
            </span>
          )}
          {user.deletedAt && (
            <div className="mt-1 text-xs text-red-300">
              Soft-deleted ({user.deletionRestriction}). Reason: {user.deletionReason ?? '\u2014'}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-xs text-gray-400 uppercase tracking-wide">Adjust chips</div>
          <div className="flex gap-2">
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount (mUSD)"
              className="flex-1 px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-sm"
            />
          </div>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason / note (audit trail)"
            className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-sm"
          />
          <div className="flex gap-2">
            <button
              disabled={busy}
              onClick={() => adjust(1)}
              className="flex-1 px-3 py-2 text-sm bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 rounded-lg disabled:opacity-50"
            >
              + Add chips
            </button>
            <button
              disabled={busy}
              onClick={() => adjust(-1)}
              className="flex-1 px-3 py-2 text-sm bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 rounded-lg disabled:opacity-50"
            >
              \u2212 Remove chips
            </button>
          </div>
        </div>

        <div className="space-y-2 border-t border-white/10 pt-4">
          <div className="text-xs text-gray-400 uppercase tracking-wide">
            {user.deletedAt ? 'Restore account' : 'Soft-delete account'}
          </div>
          {!user.deletedAt && (
            <select
              value={restriction}
              onChange={(e) => setRestriction(e.target.value)}
              className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-sm"
            >
              {RESTRICTIONS.map((r) => (
                <option key={r.value} value={r.value} className="bg-brand-bg">
                  {r.label}
                </option>
              ))}
            </select>
          )}
          {user.deletedAt ? (
            <button
              disabled={busy}
              onClick={restore}
              className="w-full px-3 py-2 text-sm bg-brand-cyan/20 hover:bg-brand-cyan/30 border border-brand-cyan/40 rounded-lg disabled:opacity-50"
            >
              Restore account
            </button>
          ) : (
            <button
              disabled={busy}
              onClick={softDelete}
              className="w-full px-3 py-2 text-sm bg-red-600/30 hover:bg-red-600/40 border border-red-500/50 rounded-lg disabled:opacity-50"
            >
              Soft-delete account
            </button>
          )}
        </div>

        {err && (
          <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
            {err}
          </div>
        )}
        {msg && (
          <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2">
            {msg}
          </div>
        )}
      </div>
    </div>
  );
}
